import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLedgerToast } from '@/hooks/useLedgerToast';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import type {
  CreateLien,
  LienholderType,
  LienStatus,
  ReadLien,
} from '@/services/api/ledgerService';
import {
  useLienCreateMutation,
  useLienUpdateMutation,
} from '@/services/api/ledgerService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import { CoverageCategory } from '@/services/types/coverage-types';

/**
 * Get the display name for a lienholder based on type.
 */
const getLienholderDisplayName = (lien: ReadLien): string => {
  if (lien.lienholder_type === 'PROVIDER' && lien.lienholder_provider) {
    return lien.lienholder_provider.name;
  }
  if (lien.lienholder_type === 'COVERAGE' && lien.lienholder_coverage) {
    return lien.lienholder_coverage.carrier_name || 'Unknown Coverage';
  }
  return 'Unknown';
};

interface LienFormData {
  lienholder_type: LienholderType;
  lienholder_id: string;
  status?: LienStatus;
}

interface LienFormDialogProps {
  matterId: string;
  lien?: ReadLien; // If provided, edit mode
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for creating or editing a lien.
 * Supports dynamic lienholder selection based on type (Provider or Coverage).
 */
export const LienFormDialog = ({
  matterId,
  lien,
  open,
  onOpenChange,
}: LienFormDialogProps) => {
  const toast = useLedgerToast();
  const isEditMode = !!lien;

  // Fetch providers and coverages for selectors
  const { data: providers = [], isLoading: isLoadingProviders } =
    useGetMedicalProvidersQuery(matterId, { skip: !open });

  // Fetch health insurance and first-party auto coverages for liens
  const {
    data: healthCoverages = [],
    isLoading: isLoadingHealthCoverages,
  } = useGetCoveragesQuery(
    { matterId, category: CoverageCategory.FIRST_PARTY_HEALTH },
    { skip: !open },
  );
  const {
    data: firstPartyCoverages = [],
    isLoading: isLoadingFirstPartyCoverages,
  } = useGetCoveragesQuery(
    { matterId, category: CoverageCategory.FIRST_PARTY },
    { skip: !open },
  );

  const coverages = useMemo(
    () => [...healthCoverages, ...firstPartyCoverages],
    [healthCoverages, firstPartyCoverages],
  );
  const isLoadingCoverages =
    isLoadingHealthCoverages || isLoadingFirstPartyCoverages;

  // API mutations
  const [createLien, { isLoading: isCreating }] = useLienCreateMutation();
  const [updateLien, { isLoading: isUpdating }] = useLienUpdateMutation();

  const isSubmitting = isCreating || isUpdating;

  // Get default lienholder ID for the form
  const getDefaultLienholderId = useCallback((): string => {
    if (!lien) return '';
    if (lien.lienholder_type === 'PROVIDER' && lien.lienholder_provider) {
      return lien.lienholder_provider.id as string;
    }
    if (lien.lienholder_type === 'COVERAGE' && lien.lienholder_coverage) {
      return lien.lienholder_coverage.id as string;
    }
    return '';
  }, [lien]);

  // Form setup
  const form = useForm<LienFormData>({
    defaultValues: {
      lienholder_type: lien?.lienholder_type ?? 'PROVIDER',
      lienholder_id: getDefaultLienholderId(),
      status: lien?.status,
    },
  });

  const { watch, setValue, reset } = form;
  const lienholderType = watch('lienholder_type');

  // Reset form when dialog opens/closes or lien changes
  useEffect(() => {
    if (open) {
      reset({
        lienholder_type: lien?.lienholder_type ?? 'PROVIDER',
        lienholder_id: getDefaultLienholderId(),
        status: lien?.status,
      });
    }
  }, [open, lien, reset, getDefaultLienholderId]);

  // Clear lienholder_id when type changes
  useEffect(() => {
    // Only clear if we're not initializing (open just changed)
    if (open && lien?.lienholder_type !== lienholderType) {
      setValue('lienholder_id', '');
    }
  }, [lienholderType, open, lien?.lienholder_type, setValue]);

  // Filtered providers (only approved ones)
  const approvedProviders = useMemo(() => {
    return providers.filter((p) => p.status === 'APPROVED');
  }, [providers]);

  // Handle form submission
  const onSubmit = async (data: LienFormData) => {
    try {
      // Auto-determine lien type: Coverage = STATUTORY, Provider = CONTRACTUAL
      const lienType =
        data.lienholder_type === 'COVERAGE' ? 'STATUTORY' : 'CONTRACTUAL';

      if (isEditMode && lien) {
        // Update existing lien
        await updateLien({
          matterId,
          id: lien.id as string,
          updateLien: {
            lien_type: lienType,
            status: data.status,
          },
        }).unwrap();
        toast.showSuccess('update', 'lien');
      } else {
        // Create new lien
        const createData: CreateLien = {
          lienholder_type: data.lienholder_type,
          lienholder_id: data.lienholder_id,
          lien_type: lienType,
        };
        await createLien({
          matterId,
          createLien: createData,
        }).unwrap();
        toast.showSuccess('create', 'lien');
      }
      onOpenChange(false);
    } catch {
      // Per plan Section 5: Server error keeps dialog open
      toast.showError();
    }
  };

  // Handle dialog close
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Lien' : 'Add Lien'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the lien details below.'
              : 'Create a new lien by selecting a lienholder and lien type.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Lienholder Type - Radio buttons (only in create mode) */}
            {!isEditMode && (
              <FormField
                control={form.control}
                name="lienholder_type"
                rules={{ required: 'Lienholder type is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lienholder Type</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="PROVIDER" id="provider" />
                          <Label htmlFor="provider" className="cursor-pointer">
                            Provider
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="COVERAGE" id="coverage" />
                          <Label htmlFor="coverage" className="cursor-pointer">
                            Coverage
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Lienholder Selector - Dynamic based on type (only in create mode) */}
            {!isEditMode && (
              <FormField
                control={form.control}
                name="lienholder_id"
                rules={{ required: 'Lienholder is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {lienholderType === 'PROVIDER' ? 'Provider' : 'Coverage'}
                    </FormLabel>
                    <FormControl>
                      {lienholderType === 'PROVIDER' ? (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={isLoadingProviders}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isLoadingProviders
                                  ? 'Loading providers...'
                                  : 'Select a provider'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {approvedProviders.length === 0 ? (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                No providers available
                              </div>
                            ) : (
                              approvedProviders.map((provider) => (
                                <SelectItem
                                  key={provider.id}
                                  value={provider.id}
                                >
                                  {provider.name || 'Unnamed Provider'}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={isLoadingCoverages}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isLoadingCoverages
                                  ? 'Loading coverages...'
                                  : 'Select a coverage'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {coverages.length === 0 ? (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                No coverages available
                              </div>
                            ) : (
                              coverages.map((coverage) => (
                                <SelectItem
                                  key={coverage.id}
                                  value={coverage.id}
                                >
                                  {coverage.carrier?.name || 'Unknown Carrier'}
                                  {coverage.policy_type
                                    ? ` - ${coverage.policy_type}`
                                    : ''}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Display lienholder info in edit mode (read-only) */}
            {isEditMode && lien && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Lienholder</Label>
                <div className="rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
                  {getLienholderDisplayName(lien)}
                  <span className="ml-2 text-muted-foreground">
                    (
                    {lien.lienholder_type === 'PROVIDER'
                      ? 'Provider'
                      : 'Coverage'}
                    )
                  </span>
                </div>
              </div>
            )}

            {/* Status Dropdown (edit mode only) */}
            {isEditMode && (
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ACTIVE">Active</SelectItem>
                          <SelectItem value="RESOLVED">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isSubmitting
                  ? isEditMode
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditMode
                    ? 'Save Changes'
                    : 'Add Lien'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
